import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import SEO from '../components/SEO';
import { BriefingIQMockup } from '../components/ProductMockups';

const features = [
  {
    title: 'Multi-location briefing centers',
    body: 'Coordinate briefing rooms, staff, and equipment across regional centers from a single dashboard.',
  },
  {
    title: 'Approval workflows',
    body: 'Customizable approval routing that fits your organisation — across sales, exec sponsors, and operations.',
  },
  {
    title: 'Customer portal',
    body: 'Branded RSVP flow, agenda sharing, and secure document exchange with visiting executives.',
  },
  {
    title: 'Post-visit measurement',
    body: 'Automated NPS, structured debriefs, and pipeline influence reporting piped back to Salesforce.',
  },
  {
    title: 'Executive scheduling',
    body: 'Availability sync with Outlook & Exchange, plus intelligent room and speaker matching.',
  },
  {
    title: 'AI-generated recaps',
    body: 'Every briefing produces a structured summary and next-best-action recommendations.',
  },
];

const BriefingIQPage = ({ onOpenDemo }) => {
  return (
    <>
      <SEO
        title="BriefingIQ — Executive Briefing Management"
        description="BriefingIQ is the executive briefing management platform for enterprise briefing centers. Coordinate multi-location visits, sync with Salesforce & Outlook, and prove pipeline impact."
        path="/briefingiq"
        keywords={['executive briefing management', 'briefing center software', 'briefing program', 'Salesforce briefings']}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'BriefingIQ', path: '/briefingiq' },
        ]}
        product={{
          name: 'BriefingIQ',
          description: 'Executive briefing management platform for enterprise briefing centers.',
          category: 'BusinessApplication',
        }}
      />

      <section className="bg-white pt-20 pb-12 lg:pt-28 lg:pb-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-5">BriefingIQ</div>
          <h1 className="headline-xl text-ink mb-6">
            Executive briefings,<br />
            <span className="text-ink-3">orchestrated end to end.</span>
          </h1>
          <p className="text-lg md:text-xl text-ink-2 max-w-2xl mx-auto leading-relaxed mb-9">
            Plan, coordinate, and execute executive briefing programs with end-to-end workflow automation. Multi-location. CRM-native. Measurable.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mb-14">
            <button onClick={onOpenDemo} className="btn-primary">
              Request a demo <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/pricing" className="btn-link">
              View pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6">
          <BriefingIQMockup />
        </div>
      </section>

      <section className="bg-[var(--bg-alt)] section">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">Capabilities</div>
            <h2 className="headline-lg text-ink">
              Everything a briefing program needs.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line-2)] rounded-3xl overflow-hidden border border-[var(--line-2)]">
            {features.map((f) => (
              <div key={f.title} className="bg-white p-8 lg:p-10">
                <Check className="w-5 h-5 text-ink mb-5" strokeWidth={2} />
                <h3 className="text-lg font-semibold text-ink mb-3">{f.title}</h3>
                <p className="text-sm text-ink-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">A closing thought</div>
          <blockquote className="headline-md text-ink mb-6">
            “BriefingIQ turned our briefing center into a measurable pipeline engine — not just a room on the calendar.”
          </blockquote>
          <div className="text-sm text-ink-3">Head of Executive Programs · Fortune 100 technology company</div>
        </div>
      </section>

      <section className="bg-[#0F172A] dark section relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="headline-lg text-white mb-6">See BriefingIQ in action.</h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={onOpenDemo} className="btn-invert">
              Request a demo <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/contact" className="btn-invert-outline">Contact sales</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default BriefingIQPage;
